import { useCallback, useEffect, useState } from "react";
import { useSharedState } from "sharedStateProvider";
import {
  CallFeedbackItem,
  CallFeedbackFiltersList,
  CallFeedbackStats,
} from "types/call-feedback-type";
import { PaginationType, emptyPagination } from "types/pagination-types";
import PageLayout from "components/page-layout";
import Table, { tableHeader } from "components/tables/table";
import { Link } from "react-router-dom";
import { oneLine, userLine } from "components/tables/table-parts";
import { listFeedback, getFeedbackStats } from "api/call-feedback";
import { getApiErrorMessage } from "api/error";

const PAGE_SIZE = 20;

function normalizeListToPagination(
  feedbacks: CallFeedbackItem[],
  total_count: number,
  limit: number,
  offset: number
): PaginationType {
  const page = limit > 0 ? Math.floor(offset / limit) + 1 : 1;
  return {
    data: feedbacks,
    page,
    size: limit,
    sort: "",
    total: total_count,
  };
}

/** Display datetime exactly as returned by API (no timezone conversion) */
function formatDateTimeRaw(isoString: string): string {
  return isoString.trim() || "—";
}

export default function CallFeedback() {
  const { notify } = useSharedState();

  const [stats, setStats] = useState<CallFeedbackStats | null>(null);
  const [list, setList] = useState<PaginationType>(emptyPagination);
  const [firstEntry, setFirstEntry] = useState(0);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);

  const [searchParams] = useState<URLSearchParams>(new URLSearchParams());

  const appendParam = useCallback((key: string, value: string) => {
    searchParams.set(key, value);
  }, [searchParams]);

  const clearFilters = useCallback(async () => {
    searchParams.delete("rating");
    searchParams.delete("search");
    searchParams.delete("user_id");
  }, [searchParams]);

  const fetchStats = useCallback(async () => {
    setIsLoadingStats(true);
    try {
      const data = await getFeedbackStats();
      setStats(data);
    } catch (error: unknown) {
      notify(
        getApiErrorMessage(error, "Ошибка при загрузке статистики отзывов"),
        "danger",
        "Ошибка статистики"
      );
    } finally {
      setIsLoadingStats(false);
    }
  }, [notify]);

  const fetchList = useCallback(
    async (page?: number) => {
      setIsLoadingList(true);
      setList(emptyPagination);
      try {
        const user_id = searchParams.get("search")?.trim() || undefined;
        const ratingParam = searchParams.get("rating");
        const rating = ratingParam ? parseInt(ratingParam, 10) : undefined;
        const offset = page !== undefined ? (page - 1) * PAGE_SIZE : 0;

        const res = await listFeedback({
          user_id,
          rating: Number.isNaN(rating) ? undefined : rating,
          limit: PAGE_SIZE,
          offset,
        });

        setList(
          normalizeListToPagination(
            res.feedbacks,
            res.total_count,
            res.limit,
            res.offset
          )
        );
      } catch (error: unknown) {
        notify(
          getApiErrorMessage(error, "Ошибка при загрузке списка отзывов"),
          "danger",
          "Ошибка загрузки отзывов"
        );
      } finally {
        setIsLoadingList(false);
      }
    },
    [notify, searchParams]
  );

  const updateFirstEntry = useCallback((value: number) => {
    setFirstEntry(value);
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const isLoading = isLoadingStats || isLoadingList;

  const tableHeaders: tableHeader[] = [
    { header_text: "№", narrow: true, wrap: true },
    { header_text: "ID", narrow: true, wrap: true },
    { header_text: "Пользователь", narrow: true, wrap: true },
    { header_text: "Сессия звонка", narrow: true, wrap: true },
    { header_text: "Оценка", narrow: true, wrap: true },
    { header_text: "Описание", narrow: true, wrap: true },
    { header_text: "Дата", narrow: true, wrap: true },
  ];

  const tableBody = (
    <>
      {list.data.map((item: CallFeedbackItem, index: number) => (
        <tr key={item.id}>
          <td>{oneLine((firstEntry + index).toString())}</td>
          <td>
            <Link to={`/call-feedback/${item.id}`} className="text-primary">
              {item.id}
            </Link>
          </td>
          <td>{userLine(item.user_id)}</td>
          <td>{oneLine(item.call_session_id || "—")}</td>
          <td>{oneLine(String(item.rating))}</td>
          <td>
            <div style={{ maxWidth: 320 }}>
              {oneLine(item.description || "—")}
            </div>
          </td>
          <td>{oneLine(formatDateTimeRaw(item.created_at))}</td>
        </tr>
      ))}
    </>
  );

  const statsCards = stats && (
    <div className="row row-deck row-cards mb-3">
      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="text-muted">Всего отзывов</div>
            <div className="h2 mb-0">
              {isLoadingStats ? "—" : stats.total_feedbacks}
            </div>
          </div>
        </div>
      </div>
      <div className="col-sm-6 col-lg-3">
        <div className="card">
          <div className="card-body">
            <div className="text-muted">Средняя оценка</div>
            <div className="h2 mb-0">
              {isLoadingStats
                ? "—"
                : stats.average_rating.toFixed(1)}
            </div>
          </div>
        </div>
      </div>
      <div className="col-sm-6 col-lg-6">
        <div className="card">
          <div className="card-body">
            <div className="text-muted mb-2">Распределение по оценкам</div>
            <div className="d-flex flex-wrap gap-3">
              {[1, 2, 3, 4, 5].map((r) => (
                <span key={r} className="text-nowrap">
                  {r}:{" "}
                  <strong>
                    {isLoadingStats ? "—" : (stats.rating_counts?.[String(r)] ?? 0)}
                  </strong>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const table = Table(
    tableHeaders,
    tableBody,
    true,
    list,
    updateFirstEntry,
    (page?: number) => fetchList(page)
  );

  return (
    <PageLayout
      children={table}
      isLoading={isLoading}
      filters={true}
      filtersList={CallFeedbackFiltersList}
      appendFilter={appendParam}
      clearFilters={clearFilters}
      search={true}
      searchAPI={() => fetchList(1)}
      customHeader={statsCards ?? undefined}
      header="Отзывы о звонках"
      subheaderText="Количество отзывов: "
      subheaderCount={list.total}
      dataLength={list.data.length}
      dateRange={false}
    />
  );
}
